/**
 * Ward API Service
 * Manages ward data using local storage
 */

import LocalStorageService from '../storage/LocalStorageService';

class WardService {
  constructor() {
    this.storageKey = 'wards';
  }

  /**
   * Get all wards
   */
  async getAll() {
    try {
      const wards = LocalStorageService.get(this.storageKey) || [];
      return { success: true, data: wards };
    } catch (error) {
      console.error('Error getting all wards:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get ward by ID
   */
  async getById(wardId) {
    try {
      const ward = LocalStorageService.findById(this.storageKey, wardId);
      if (!ward) {
        return { success: false, error: 'Ward not found' };
      }
      return { success: true, data: ward };
    } catch (error) {
      console.error('Error getting ward by ID:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get wards by facility ID
   */
  async getByFacilityId(facilityId) {
    try {
      const allWards = LocalStorageService.get(this.storageKey) || [];
      const facilityWards = allWards.filter(ward => ward.facilityId === facilityId);
      return { success: true, data: facilityWards };
    } catch (error) {
      console.error('Error getting wards by facility:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a new ward
   */
  async create(wardData) {
    try {
      const { createWard } = require('../../data/models/Ward');
      const newWard = createWard(wardData);
      
      const added = LocalStorageService.addItem(this.storageKey, newWard);
      return { success: true, data: added };
    } catch (error) {
      console.error('Error creating ward:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update a ward
   */
  async update(wardId, updates) {
    try {
      const updated = LocalStorageService.updateItem(this.storageKey, wardId, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      
      if (!updated) {
        return { success: false, error: 'Ward not found' };
      }
      
      return { success: true, data: updated };
    } catch (error) {
      console.error('Error updating ward:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a ward
   */
  async delete(wardId) {
    try {
      const deleted = LocalStorageService.deleteItem(this.storageKey, wardId);
      if (!deleted) {
        return { success: false, error: 'Ward not found' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting ward:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete all wards for a facility
   */
  async deleteByFacilityId(facilityId) {
    try {
      const allWards = LocalStorageService.get(this.storageKey) || [];
      const facilityWards = allWards.filter(ward => ward.facilityId === facilityId);
      
      for (const ward of facilityWards) {
        await this.delete(ward.id);
      }
      
      return { success: true, deletedCount: facilityWards.length };
    } catch (error) {
      console.error('Error deleting wards by facility:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new WardService();

